// alert-details.js

import uuid from "uuid";
import get from "lodash/get";
import { groupByEnum } from "alerts/components/AlertWizard/Steps/Conditions/Consts.js";

const createCondition = () => {
  return {
    id: uuid(),
    type: "above",
    threshold: 20,
    duration: 5,
    summaryFunction: "average",
    metricName: "",
    target: "duration",
    tags: [],
    groupTags: [],
    serviceName: "",
    groupBy: groupByEnum.ANY,
  };
};

const createInitalState = () => {
  const condition = createCondition();
  let state = {
    id: undefined,
    name: undefined,
    alertType: undefined,
    notificationInterval: 10,
    notificationServices: [],
    conditions: {
      byId: {},
      currentConditionId: condition.id,
    },
    runbookUrl: undefined,
    description: undefined,
    active: true,
  };
  state.conditions.byId[condition.id] = condition;
  return state;
};

const mapToCondition = condition => {
  const tagsetExpression = condition.node.tagsetExpression || [];
  const duration = condition.node.duration;

  let groupBy;
  const groupTags = tagsetExpression
    .filter(tag => {
      return tag.grouped === true;
    })
    .map(tag => {
      return tag.name;
    });

  const nonGroupTags = tagsetExpression
    .filter(tag => {
      return tag.values !== undefined;
    })
    .map(tag => {
      return {
        name: tag.name,
        values: tag.values.filter(
          value => value !== condition.node.serviceName
        ),
      };
    })
    .filter(tag => tag.values.length !== 0);

  if (
    groupTags.some(e => {
      return e === "*";
    })
  ) {
    groupBy = groupByEnum.ALL;
  } else if (groupTags.length === 0) {
    groupBy = groupByEnum.ANY;
  } else {
    groupBy = groupByEnum.CUSTOM;
  }

  return {
    id: condition.node.id,
    type: condition.node.conditionType,
    threshold: !condition.node.threshold ? 0 : condition.node.threshold,
    duration: duration ? Math.round(duration / 60) : null,
    summaryFunction: condition.node.summaryFunction,
    metricName: condition.node.metricName,
    serviceName: condition.node.serviceName,
    target: condition.node.duration ? "duration" : "immediately",
    groupBy: groupBy,
    groupTags: groupTags,
    tags: nonGroupTags.map(value => ({
      name: value["name"],
      values: value["values"],
      grouped: !!value["grouped"], // return false if grouped is null
    })),
  };
};
const createAlert = alert => {
  let state = {
    id: alert.id,
    alertType: alert.alertType,
    name: alert.name,
    description: alert.description,
    notificationInterval: Math.round(alert.rearmSeconds / 60),
    notificationServices: get(alert.notificationServices, "edges", []).map(e =>
      parseInt(e.node.id)
    ),
    conditions: {
      byId: {},
      currentConditionId: undefined,
    },

    runbookUrl: alert.runbookUrl,
    active: alert.active,
    status: alert.status,
  };

  const conditions = get(alert.conditions, "edges", {});

  if (conditions && Array.isArray(conditions)) {
    conditions.forEach(condition => {
      const mappedCondition = mapToCondition(condition);
      state.conditions.byId[mappedCondition.id] = mappedCondition;
    });
  }
  return state;
};

export const INITIAL_STATE = createInitalState();

// action types
export const SET_TYPE = "unido-web/alert-details/SET_TYPE";
export const ADD_NOTIFICATION_SERVICE =
  "unido-web/alert-details/ADD_NOTIFICATION_SERVICE";
export const DELETE_NOTIFICATION_SERVICE =
  "unido-web/alert-details/DELETE_NOTIFICATION_SERVICE";
export const ADD_CONDITION = "unido-web/alert-details/ADD_CONDITION";
export const SET_NOTIFICATION_INTERVAL =
  "unido-web/alert-details/SET_NOTIFICATION_INTERVAL";
export const SET_NAME = "unido-web/alert-details/SET_NAME";
export const SET_DESCRIPTION = "unido-web/alert-details/SET_DESCRIPTION";
export const SET_RUNBOOK_URL = "unido-web/alert-details/SET_RUNBOOK_URL";
export const RESET_ALERT_STATE = "unido-web/alert-details/RESET_ALERT_STATE";
export const SET_CURRENT_CONDITION_ID =
  "unido-web/alert-details/SET_CURRENT_CONDITION_ID";
export const REMOVE_CONDITION = "unido-web/alert-details/REMOVE_CONDITION";
export const SET_CONDITION_VALUE =
  "unido-web/alert-details/SET_CONDITION_VALUE";
export const RESET_CONDITION = "unido-web/alert-details/RESET_CONDITION";
export const DISCARD_CONDITION = "unido-web/alert-details/DISCARD_CONDITION";
export const SET_ALERT = "unido-web/alert-details/SET_ALERT";
export const SET_ALERT_ACTIVE = "unido-web/alert-details/SET_ALERT_ACTIVE";

export const updateCondition = (conditions, key, value) => {
  let { byId, currentConditionId } = conditions;
  let condition = Object.assign({}, byId[currentConditionId]);
  // update condition
  condition[key] = value;

  return {
    ...byId,
    [currentConditionId]: condition,
  };
};

// reducer
const alertDetailsReducer = (state = INITIAL_STATE, action) => {
  let conditionsById = null;

  switch (action.type) {
    case SET_TYPE:
      return {
        ...state,
        alertType: action.alertType,
      };
    case ADD_NOTIFICATION_SERVICE:
      return {
        ...state,
        notificationServices: [...state.notificationServices, action.serviceId],
      };
    case DELETE_NOTIFICATION_SERVICE:
      return {
        ...state,
        notificationServices: state.notificationServices.filter(
          serviceId => serviceId !== action.serviceId
        ),
      };
    case SET_NOTIFICATION_INTERVAL:
      return {
        ...state,
        notificationInterval: action.time,
      };
    case SET_NAME:
      return {
        ...state,
        name: action.name,
      };
    case SET_DESCRIPTION:
      return {
        ...state,
        description: action.description,
      };
    case SET_RUNBOOK_URL:
      return {
        ...state,
        runbookUrl: action.runbookUrl,
      };
    case RESET_ALERT_STATE:
      return INITIAL_STATE;

    // conditions
    case ADD_CONDITION: {
      let conditionsById = state.conditions.byId;

      // add the condition
      let condition = createCondition();
      conditionsById[condition.id] = condition;

      return {
        ...state,
        conditions: {
          ...state.conditions,
          byId: conditionsById,
          currentConditionId: condition.id,
        },
      };
    }
    case RESET_CONDITION: {
      let { byId, currentConditionId } = state.conditions;
      let condition = Object.assign({}, byId[currentConditionId]);
      condition = createCondition();
      condition.id = currentConditionId;

      return {
        ...state,
        conditions: {
          ...state.conditions,
          byId: {
            ...byId,
            [currentConditionId]: condition,
          },
        },
      };
    }

    case DISCARD_CONDITION: {
      let { currentConditionId, byId } = state.conditions;

      return {
        ...state,
        conditions: {
          ...state.conditions,
          byId: {
            ...byId,
            [currentConditionId]: action.conditionSnapshot,
          },
        },
      };
    }

    case SET_CURRENT_CONDITION_ID:
      return {
        ...state,
        conditions: {
          ...state.conditions,
          currentConditionId: action.currentConditionId,
        },
      };
    case SET_CONDITION_VALUE:
      conditionsById = updateCondition(
        state.conditions,
        action.key,
        action.value
      );

      return {
        ...state,
        conditions: {
          ...state.conditions,
          byId: conditionsById,
        },
      };
    case REMOVE_CONDITION:
      conditionsById = Object.assign({}, state.conditions.byId);

      // remove the condition
      delete conditionsById[action.conditionId];

      return {
        ...state,
        conditions: {
          ...state.conditions,
          byId: conditionsById,
        },
      };
    case SET_ALERT:
      var editAlertState = createAlert(action.alert);
      return editAlertState;
    case SET_ALERT_ACTIVE:
      return {
        ...state,
        active: action.isActive,
      };
    default:
      return state;
  }
};

export default alertDetailsReducer;

// action creators
export const setAlertType = alertType => {
  return {
    type: SET_TYPE,
    alertType,
  };
};
export const addAlertCondition = condition => {
  return {
    type: ADD_CONDITION,
    condition,
  };
};

export const resetAlertState = () => {
  return {
    type: RESET_ALERT_STATE,
  };
};

export const setNotificationInterval = time => {
  return {
    type: SET_NOTIFICATION_INTERVAL,
    time,
  };
};

export const addNotficationService = serviceId => {
  return {
    type: ADD_NOTIFICATION_SERVICE,
    serviceId,
  };
};
export const deleteNotficationService = serviceId => {
  return {
    type: DELETE_NOTIFICATION_SERVICE,
    serviceId,
  };
};

export const setAlertName = name => {
  return {
    type: SET_NAME,
    name,
  };
};

export const setAlertDescription = description => {
  return {
    type: SET_DESCRIPTION,
    description,
  };
};

export const setAlertRunbookUrl = runbookUrl => {
  return {
    type: SET_RUNBOOK_URL,
    runbookUrl,
  };
};
export const resetCondition = () => {
  return {
    type: RESET_CONDITION,
  };
};

export const discardCondition = conditionSnapshot => {
  return {
    type: DISCARD_CONDITION,
    conditionSnapshot,
  };
};

export const setConditionValue = (key, value) => {
  return {
    type: SET_CONDITION_VALUE,
    key,
    value,
  };
};

export const setCurrentConditionId = currentConditionId => {
  return { type: SET_CURRENT_CONDITION_ID, currentConditionId };
};

export const addNewCondtion = () => {
  return { type: ADD_CONDITION };
};

export const removeCondition = conditionId => {
  return { type: REMOVE_CONDITION, conditionId };
};

export const setAlert = alert => {
  return { type: SET_ALERT, alert };
};

export const setActive = isActive => {
  return { type: SET_ALERT_ACTIVE, isActive };
};
